import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/AxiosService';
import getBITSEndApiMap from '../apiMap';

export interface QuizQuestion {
  slot: number;
  type: string;
  page: number;
  html: string;
  sequencecheck: number;
  flagged: boolean;
  number: number;
  status: string;
  mark: string;
  maxmark: number;
}

export interface QuizAttempt {
  id: number;
  quiz: number;
  userid: number;
  attempt: number;
  uniqueid: number;
  layout: string;
  currentpage: number;
  preview: number;
  state: string;
  timestart: number;
  timefinish: number;
  timemodified: number;
  timecheckstate: number;
  sumgrades: number;
}

export interface StartAttemptResponse {
  success: boolean;
  data: {
    attempt: QuizAttempt;
    messages: any[];
    nextpage: number;
  };
}

export interface AttemptDataResponse {
  success: boolean;
  data: {
    attempt: QuizAttempt;
    questions: QuizQuestion[];
    nextpage: number | null;
    messages: any[];
  };
}

export interface ProcessAttemptResponse {
  success: boolean;
  data: {
    state: string;
    messages: any[];
    warnings: any[];
  };
}

// Start quiz attempt
export const startQuizAttempt = async (data: {
  contextId: string;
  quizId: number;
}): Promise<StartAttemptResponse> => {
  const url = getBITSEndApiMap('startQuizAttempt');
  const response = await api.post<StartAttemptResponse>(url, data);
  return response.data;
};

// Get attempt data
export const getQuizAttemptData = async (
  contextId: string,
  attemptId: number,
  page: number = 0
): Promise<AttemptDataResponse> => {
  const url = getBITSEndApiMap('getQuizAttemptData').replace(':attemptId', attemptId.toString());
  const response = await api.get<AttemptDataResponse>(url, {
    params: { contextId, page }
  });
  return response.data;
};

// Process attempt (submit answers)
export const processQuizAttempt = async (data: {
  contextId: string;
  attemptId: number;
  answers: Record<string, any>;
  finishAttempt: boolean;
}): Promise<ProcessAttemptResponse> => {
  const { attemptId, ...body } = data;
  const url = getBITSEndApiMap('processQuizAttempt').replace(':attemptId', attemptId.toString());
  const response = await api.post<ProcessAttemptResponse>(url, body);
  return response.data;
};

// React Query Hooks
export const useStartQuizAttempt = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: startQuizAttempt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quizAttempts'] });
    }
  });
};

export const useQuizAttemptData = (
  contextId: string | null,
  attemptId: number | null,
  page: number = 0
) => {
  return useQuery({
    queryKey: ['quizAttemptData', attemptId, page],
    queryFn: () => getQuizAttemptData(contextId!, attemptId!, page),
    enabled: !!attemptId && !!contextId,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0  // Don't cache quiz data
  });
};

export const useProcessQuizAttempt = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: processQuizAttempt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quizAttempts'] });
    }
  });
};

export interface ReviewQuestion {
  slot: number;
  state: string;  // 'gradedright' | 'gradedwrong' | 'gradedpartial' | 'todo' | 'invalid'
  mark: string;
  maxmark: number;
  number: number;
  type: string;
}

export interface ReviewAttemptResponse {
  success: boolean;
  data: {
    attempt: QuizAttempt;
    grade: string;
    sumgrades: number;
    questions: ReviewQuestion[];
  };
}

export const getQuizAttemptReview = async (
  contextId: string,
  attemptId: number
): Promise<ReviewAttemptResponse> => {
  const url = getBITSEndApiMap('getQuizAttemptReview').replace(':attemptId', attemptId.toString());
  const response = await api.get<ReviewAttemptResponse>(url, {
    params: { contextId }
  });
  return response.data;
};

export interface AttemptHistoryRecord {
  id: number;
  attempt: number;
  state: string;
  timestart: number;
  timefinish: number;
  sumgrades: number | null;
}

export interface AttemptHistoryResponse {
  success: boolean;
  data: {
    attempts: AttemptHistoryRecord[];
  };
}

export const getUserAttemptHistory = async (
  contextId: string,
  quizId: number
): Promise<AttemptHistoryResponse> => {
  const url = getBITSEndApiMap('getUserAttemptHistory');
  const response = await api.get<AttemptHistoryResponse>(url, {
    params: { contextId, quizId }
  });
  return response.data;
};

export const useQuizAttemptHistory = (
  contextId: string | null,
  quizId: number | null | undefined
) => {
  return useQuery({
    queryKey: ['quizAttemptHistory', contextId, quizId],
    queryFn: () => getUserAttemptHistory(contextId!, quizId!),
    enabled: !!contextId && !!quizId,
    staleTime: 0,
    gcTime: 0
  });
};
